import React, { useState, useEffect } from "react";
import "../css/DeviceUpdatePage.css";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import CustomButton from "./CustomButton";

const DeviceUpdatePage = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${BASE_URL}/api/states`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => setStates(data || []))
      .catch((error) => console.error("Error fetching states:", error));
  }, []);

  useEffect(() => {
    if (selectedState) {
      fetch(`${BASE_URL}/api/divisions?state=${selectedState}`, {
        headers: getAuthHeaders(),
      })
        .then((res) => res.json())
        .then((data) => setDivisions(data || []))
        .catch((error) => console.error("Error fetching divisions:", error));
    } else {
      setDivisions([]);
      setSelectedDivision("");
    }
  }, [selectedState]);

  const handleFilter = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (selectedState) params.append("state_id", selectedState);
      if (selectedDivision) params.append("division_id", selectedDivision);
      params.append("is_device", true);

      const response = await fetch(
        `${BASE_URL}/api/employees?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) throw new Error("Failed to fetch filtered employees");

      const data = await response.json();
      setFilteredEmployees(data);
    } catch (error) {
      console.error("Error fetching filtered employees:", error);
      setError("Failed to load employees. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleDeviceStatus = async (deviceId, isActive, employeeId) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${BASE_URL}/api/devices/${deviceId}/toggle-status?employee_id=${employeeId}`,
        {
          method: "PUT",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ is_active: isActive }),
        }
      );

      if (!response.ok) throw new Error("Failed to update device status");

      // Update UI state
      setFilteredEmployees((prev) =>
        prev.map((emp) =>
          emp.id === employeeId
            ? {
                ...emp,
                devices: emp.devices.map((device) =>
                  device.id === deviceId
                    ? { ...device, is_active: isActive }
                    : device
                ),
              }
            : emp
        )
      );
    } catch (error) {
      console.error("Toggle device status failed:", error);
      alert("Failed to update device status.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Device Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Device Report</h2>
          <table>
            <thead className="text-white">
              <tr>
                <th>S.No</th>
                <th>Employee</th>
                <th>Division</th>
                <th>Manager</th>
                <th>Designation</th>
                <th>State</th>
                <th>City</th>
                <th>Device ID(s)</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              ${filteredEmployees
                .map(
                  (emp, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${emp.first_name} ${emp.last_name}</td>
                  <td>${emp.division || "N/A"}</td>
                  <td>${emp.manager || "N/A"}</td>
                  <td>${emp.designation || "N/A"}</td>
                  <td>${emp.state_name || "N/A"}</td>
                  <td>${emp.city_name || "N/A"}</td>
                  <td>${
                    emp.devices && emp.devices.length > 0
                      ? emp.devices.map((dev) => dev.device_id).join(", ")
                      : "No Devices"
                  }</td>
                  <td>${
                    emp.devices && emp.devices.some((dev) => dev.is_active)
                      ? emp.devices
                          .filter((dev) => dev.is_active)
                          .map((dev) => dev.device_id)
                          .join(", ")
                      : "No"
                  }</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleExportExcel = () => {
    const headers = [
      "S.No",
      "Employee",
      "Division",
      "Manager",
      "Designation",
      "State",
      "City",
      "Device IDs",
      "Active Devices",
    ].join(",");

    const rows = filteredEmployees.map((emp, index) =>
      [
        index + 1,
        `"${emp.first_name} ${emp.last_name || ""}"`,
        `"${emp.division || "N/A"}"`,
        `"${emp.manager || "N/A"}"`,
        `"${emp.designation || "N/A"}"`,
        `"${emp.state_name || "N/A"}"`,
        `"${emp.city_name || "N/A"}"`,
        `"${
          emp.devices && emp.devices.length > 0
            ? emp.devices.map((dev) => dev.device_id).join(", ")
            : "No Devices"
        }"`,
        `"${
          emp.devices && emp.devices.some((dev) => dev.is_active)
            ? emp.devices
                .filter((dev) => dev.is_active)
                .map((dev) => dev.device_id)
                .join(", ")
            : "No"
        }"`,
      ].join(",")
    );

    const csvContent = [headers, ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `device_report_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className=" p-2">
      {isLoading && <Loader />}

      <div className={`${isLoading ? "blur-sm pointer-events-none" : ""}`}>
        {/* Filter Section */}
        <div className="flex flex-wrap gap-4 items-center justify-center p-4 bg-gray-100 border rounded-lg shadow-md mb-6">
          <div>
            <label className="font-semibold mr-2">State:</label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="border px-3 py-2 rounded-md bg-white min-w-[160px]"
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-semibold mr-2">Division:</label>
            <select
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              disabled={!selectedState}
              className="border px-3 py-2 rounded-md bg-white min-w-[160px] disabled:bg-gray-200"
            >
              <option value="">Select Division</option>
              {divisions.map((div) => (
                <option key={div.id} value={div.id}>
                  {div.name}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleFilter}
            className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-md shadow font-semibold"
          >
            GO
          </button>
        </div>

        {/* Table */}
        {filteredEmployees.length > 0 && (
          <>
            <div className="border rounded shadow  table-wrapper">
              <table className="unlock-dcr-leave-table text-sm ">
                <thead className="bg-blue-500 text-white">
                  <tr>
                    <th className="w-10">S.No</th>
                    <th>Employee</th>
                    <th>Division</th>
                    <th>Manager</th>
                    <th>Designation</th>
                    <th>State</th>
                    <th>City</th>
                    <th className="min-w-[250px]">Device IDs</th>
                    <th className="w-[80px]">Active</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEmployees.map((emp, index) => (
                    <tr key={emp.id} className="border-b hover:bg-gray-50">
                      <td className="text-center">{index + 1}</td>
                      <td>
                        {emp.first_name} {emp.last_name}
                      </td>
                      <td>{emp.division || "N/A"}</td>
                      <td>{emp.manager || "N/A"}</td>
                      <td>{emp.designation || "N/A"}</td>
                      <td>{emp.state_name || "N/A"}</td>
                      <td>{emp.city_name || "N/A"}</td>

                      {/* Device List w/ Toggle (UNCHANGED LOGIC) */}
                      <td className="space-y-2">
                        {emp.devices?.length ? (
                          emp.devices.map((device, i) => (
                            <div
                              key={device.id || i}
                              className="flex items-center justify-between border rounded-md bg-white px-2 py-1 shadow-sm"
                            >
                              <span className="text-sm text-gray-700">
                                {i + 1}. {device.device_id}
                              </span>

                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={device.is_active}
                                  onChange={(e) =>
                                    handleToggleDeviceStatus(
                                      device.id,
                                      e.target.checked,
                                      emp.id
                                    )
                                  }
                                />
                                <div className="relative w-10 h-5 bg-gray-300 rounded-full transition peer-checked:bg-green-500">
                                  <span className="absolute left-1 top-0.5 w-4 h-4 bg-white rounded-full shadow transform peer-checked:translate-x-5 transition"></span>
                                </div>
                              </label>
                            </div>
                          ))
                        ) : (
                          <span className="text-gray-500 italic">
                            No Devices
                          </span>
                        )}
                      </td>

                      {/* Active Count */}
                      <td className="text-center">
                        {emp.devices?.some((d) => d.is_active)
                          ? emp.devices.filter((d) => d.is_active).length
                          : "❌"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-center gap-4">
              <CustomButton onClick={handlePrint} label="Print" variant="print">
                🖨
              </CustomButton>

              <CustomButton
                onClick={handleExportExcel}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeviceUpdatePage;
